import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { api } from "../api";

// The question: what in the lab needs someone, most urgent first?

export interface Alert {
  id: string;
  severity: "critical" | "warning" | "info";
  kind: string;
  project: string;
  projectName: string;
  env: string | null;
  title: string;
  detail: string;
  action: string;
}

const SEVERITY = {
  critical: { label: "Critical", pill: "negative" },
  warning: { label: "Warning", pill: "warning" },
  info: { label: "Info", pill: "" },
} as const;

const KIND_WORDS: Record<string, string> = {
  down: "Down",
  "partly-up": "Partly down",
  stopped: "Stopped, still billed",
  expired: "Demo expired",
  expiring: "Demo expiring",
  "no-repo": "No repository",
  "no-owner": "No owner",
  "credential-outside-vault": "Secret outside the vault",
  "no-url": "No URL",
};

export function useAlerts() {
  return useQuery({ queryKey: ["alerts"], queryFn: () => api<Alert[]>("/alerts"), refetchInterval: 60_000 });
}

export function AlertsPage() {
  const q = useAlerts();
  const [sev, setSev] = useState<"all" | Alert["severity"]>("all");
  const [kind, setKind] = useState("");
  const counts = useMemo(() => {
    const c = { all: 0, critical: 0, warning: 0, info: 0 } as Record<string, number>;
    for (const a of q.data ?? []) { c.all = (c.all ?? 0) + 1; c[a.severity] = (c[a.severity] ?? 0) + 1; }
    return c;
  }, [q.data]);
  const kinds = useMemo(() => [...new Set((q.data ?? []).map((a) => a.kind))], [q.data]);
  const rows = (q.data ?? []).filter((a) => (sev === "all" || a.severity === sev) && (!kind || a.kind === kind));

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Alerts</h1>
          <p className="sub">
            {q.data
              ? counts.critical
                ? `${counts.critical} critical, ${counts.warning} warnings across ${new Set(q.data.map((a) => a.project)).size} projects.`
                : `Nothing critical. ${counts.warning} warnings, ${counts.info} notes.`
              : "What needs someone, most urgent first."}
          </p>
        </div>
        <select className="input" style={{ height: 32, width: 220 }} aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Every kind</option>
          {kinds.map((k) => <option key={k} value={k}>{KIND_WORDS[k] ?? k}</option>)}
        </select>
      </div>

      <div className="tabs" role="tablist" aria-label="Severity">
        {(["all", "critical", "warning", "info"] as const).map((s) => (
          <button key={s} role="tab" className="tab" aria-selected={sev === s} onClick={() => setSev(s)}>
            {s === "all" ? "All" : SEVERITY[s].label}<span className="n">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      <section className="panel flush" aria-label="Alerts">
        {q.error && <div className="item"><span className="error">Couldn't load alerts: {q.error.message}</span></div>}
        <div className="list">
          {q.isPending && [0, 1, 2].map((i) => <div className="item" key={i}><span className="skeleton" style={{ width: 320, height: 14 }} /></div>)}
          {rows.map((a) => (
            <div className="item" key={a.id} style={{ gridTemplateColumns: "96px minmax(0, 1fr) auto" }}>
              <span className={`pill ${SEVERITY[a.severity].pill}`} style={{ justifySelf: "start" }}>{SEVERITY[a.severity].label}</span>
              <div>
                <div className="cell-main">{a.title}</div>
                <div className="cell-sub">
                  <Link to={`/p/${a.project}`} style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>{a.projectName.split(" — ")[0]}</Link>
                  {a.env ? <span className="mono"> · {a.env}</span> : ""} · {a.detail}
                </div>
              </div>
              <span className="small" style={{ textAlign: "right" }}>{a.action}</span>
            </div>
          ))}
          {q.data && rows.length === 0 && <div className="item"><span className="muted">No alerts here.</span></div>}
        </div>
      </section>
    </div>
  );
}
